import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { NAFGTMEvent } from '@appTypes/GTM/NAFGTMEvent';
import { useGTMDataLayer } from '@hooks/GTM/useGTMDataLayer';
import useSelector from '@redux/typedHooks';
import ErrorBoundary from '@components/error-component/ErrorBoundary';
import { TabBody } from './TabBody';
import * as S from './TabContainer.styles';
import { TabContainerProps } from './TabContainer.types';
import { getTabIndex } from './TabContainer.utils';

const TabContainer = ({ tabs, isLeftColumn, slug, tabTitle, onTabChange }: TabContainerProps) => {
  const [selectedTabIndex, setSelectedTabIndex] = useState(getTabIndex(tabs, isLeftColumn, tabTitle));

  const dataLayer = useGTMDataLayer();
  const location = useLocation();
  const baseUrl = useSelector((state) => state.application.baseUrl);

  const handleHeaderClick = (e: React.MouseEvent<HTMLButtonElement>, tabIndex: number) => {
    e.preventDefault();

    if (dataLayer) {
      dataLayer.push({
        event: NAFGTMEvent.tabNavigation,
        tabNavigation: {
          from: tabs[selectedTabIndex]?.title || 'UNDEFINED',
          to: tabs[tabIndex]?.title || 'ERROR',
        },
        page: location.pathname,
        pageUrl: `${baseUrl}${location.pathname}`,
      });
    }

    onTabChange?.(tabs[tabIndex]?.title);
    setSelectedTabIndex(tabIndex);
  };

  // Rerender if tab list or selected tab title changes and that makes
  // a difference to the index
  useEffect(() => {
    const newIndex = getTabIndex(tabs, isLeftColumn, tabTitle);
    if (newIndex !== selectedTabIndex) {
      setSelectedTabIndex(newIndex);
    }
  }, [tabs, tabTitle, isLeftColumn, selectedTabIndex]);

  return (
    <div>
      <S.ScrollContainer>
        <S.HeaderBorder>
          <S.TabHeader role="tablist">
            {tabs.map((tab, tabIndex) =>
              tab.column === 'right' ? (
                <S.RightTabHeaderButton
                  key={tab.title}
                  $isLeftColumn={isLeftColumn}
                  $active={selectedTabIndex === tabIndex}
                  type="button"
                  onClick={(e) => handleHeaderClick(e, tabIndex)}
                  role="tab"
                  aria-selected={selectedTabIndex === tabIndex}
                  aria-controls={tab.title}
                  id={`tab-${isLeftColumn ? 'left' : 'right'}-${tab.title}`}
                >
                  {tab.title}
                </S.RightTabHeaderButton>
              ) : (
                <S.BaseTabHeaderButton
                  key={tab.title}
                  $active={selectedTabIndex === tabIndex}
                  type="button"
                  onClick={(e) => handleHeaderClick(e, tabIndex)}
                  role="tab"
                  aria-selected={selectedTabIndex === tabIndex}
                  aria-controls={tab.title}
                  id={`tab-${isLeftColumn ? 'left' : 'right'}-${tab.title}`}
                >
                  {tab.title}
                </S.BaseTabHeaderButton>
              ),
            )}
          </S.TabHeader>
        </S.HeaderBorder>
      </S.ScrollContainer>
      <ErrorBoundary>
        <S.SectionContainer
          role="tabpanel"
          aria-labelledby={`tab-${isLeftColumn ? 'left' : 'right'}-${tabs[selectedTabIndex]?.title}`}
        >
          <TabBody tab={tabs[selectedTabIndex]} slug={slug} />
        </S.SectionContainer>
      </ErrorBoundary>
    </div>
  );
};

export { TabContainer };
